import { useIntl, useStaticUrls } from 'platform-components';
import { getExactPathMatch } from 'platform-components/utils';
import qs from 'query-string';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import { AssignmentEmptyList } from 'common.components/lesson-view.assignment/__empty-list/assignment__empty-list';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import { getHasNoActiveTeams } from 'common.components/user/selectors';
import { getIsInStudentRegistrationLinkExperiment } from 'common.components/user/selectors';
import { YM_LOCATION } from 'utils/ym-constants';

import { getShouldShowAddSchoolControl } from '../selectors';

interface Props {
    showError: boolean;
}

export const AssignmentAddSchoolControl = ({ showError }: Props) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { search, pathname } = useLocation();
    const query = qs.parse(search);
    const shouldShowAddSchoolControl = useSelector(getShouldShowAddSchoolControl);
    const inStudentLinkExperiment = useSelector(getIsInStudentRegistrationLinkExperiment);
    const hasNoTeams = useSelector(getHasNoActiveTeams);
    const staticUrls = useStaticUrls();
    const params = useParams<Record<string, string>>();

    const startTeamBuilder = useCallback(() => {
        if (hasNoTeams) {
            const { path = '' } = getExactPathMatch(staticUrls, pathname) || {};
            ym('reachGoal', 'click_add_class', { from: path, subject: params?.subject, grade: query?.grade });

            dispatch(
                teamBuilderActions.start({
                    entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
                    location: YM_LOCATION.LESSON_VIEW,
                    defaultGrade: Number(query?.grade),
                    canEditGrade: false,
                })
            );
        } else {
            dispatch(
                teamBuilderActions.start({
                    entryPoint: inStudentLinkExperiment
                        ? TEAM_BUILDER_ENTRY_STEPS.SCHOOL
                        : TEAM_BUILDER_ENTRY_STEPS.STUDENTS,
                    location: YM_LOCATION.LESSON_VIEW,
                })
            );
        }
    }, [dispatch, hasNoTeams, inStudentLinkExperiment, params?.subject, pathname, query?.grade, staticUrls]);

    const errorMessage = inStudentLinkExperiment
        ? 'lessonView.assignment.accordionItem.noSchoolAndTeamError.inStudentLinkExperiment'
        : 'lessonView.assignment.accordionItem.noSchoolAndTeamError';

    const placeholder = inStudentLinkExperiment
        ? 'lessonView.assignment.accordionItem.schoolAndStudents.inStudentLinkExperiment'
        : 'lessonView.assignment.accordionItem.schoolAndStudents';

    return (
        <>
            {shouldShowAddSchoolControl && (
                <AssignmentEmptyList
                    addText="lessonView.assignment.accordionItem.addSchool"
                    errorMessage={showError ? errorMessage : null}
                    onAddClick={startTeamBuilder}
                    placeholder={placeholder}
                    title={intl.formatMessage({ id: 'lessonView.assignment.accordionItem.students' })}
                />
            )}
        </>
    );
};
